import { ResultOf } from "gql.tada";
import { ProductMotionSlidesSectionById } from "../../../queries/entries/ProductMotionSlidesSectionById";
import { useId, useMemo, useRef } from "react";
import { useScroll, useTransform, motion } from "motion/react";
import { isValidLevel, isValidPalette } from "../../../types/ThemeModel";
import { useTheme } from "../../../hooks/useTheme";
import { useLenis } from "lenis/react";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { SlideControls } from "./SlideControls";
import {
  ProductMotionInitialSlide,
  ProductMotionSlide,
} from "./ProductMotionSlide";

export const ProductMotionSlidesSection = ({
  entry: { productMotionSlidesSection: entry },
}: {
  entry: ResultOf<typeof ProductMotionSlidesSectionById>;
}) => {
  const { theme: initialTheme } = useTheme();
  const isDesktop = useIsDesktop();

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
  });

  const slideCount = (entry?.slidesCollection?.items.length ?? 0) + 1;

  const transparent = "rgba(0, 0, 0 / 0)";
  const slideThemes = useMemo(
    () =>
      (entry?.slidesCollection?.items ?? []).map((item, index) => {
        const page = item?.ctaLink?.pageReference;

        const theme =
          (page?.__typename === "Page" ? page.theme : initialTheme) ??
          initialTheme;

        const { palette, foreground, background } = theme;

        if (
          !isValidPalette(palette) ||
          !isValidLevel(foreground) ||
          !isValidLevel(background)
        )
          throw new Error(
            `ProductMotionSlidesSection: Invalid theme for slide #${index}`
          );

        const bodyStyles = window.getComputedStyle(document.body);

        return {
          background: `rgb(${bodyStyles.getPropertyValue(
            `--palette-${palette}-${background}`
          )} / 1)`,
          foreground: `rgb(${bodyStyles.getPropertyValue(
            `--palette-${palette}-${foreground}`
          )} / 1)`,
        };
      }),
    [entry, initialTheme]
  );

  const segmentMultiplier = 4;
  const segmentCount = slideCount * segmentMultiplier;
  const backgroundColor = useTransform(
    scrollYProgress,
    [
      ...Array.from(
        { length: segmentCount },
        (_, index) => index / segmentCount
      ),
      1,
    ],
    [
      transparent,
      // append intro slide
      ...[slideThemes[0], ...slideThemes]
        .map(({ background }, index) => {
          if (index === slideThemes.length) {
            return [
              ...Array.from(
                { length: segmentMultiplier - 1 },
                () => background
              ),
              transparent,
            ];
          } else {
            return Array.from({ length: segmentMultiplier }, () => background);
          }
        })
        .flat(),
    ]
  );

  const slideControlsMotionValue = useTransform(
    scrollYProgress,
    [
      ...Array.from(
        { length: segmentCount },
        (_, index) => index / segmentCount
      ),
      1,
    ],
    [
      -1,
      ...Array.from({ length: slideCount }, (_, index) => {
        if (index === slideCount - 1) {
          return [
            ...Array.from({ length: segmentMultiplier - 1 }, () => index - 1),
            slideCount + 1,
          ];
        } else {
          return Array.from({ length: segmentMultiplier }, () => index - 1);
        }
      }).flat(),
    ]
  );

  const lenis = useLenis();
  const slideGroupId = useId();

  if (!entry) return null;

  return (
    <motion.section
      className="w-full bg-brand-black container-base relative"
      style={{ backgroundColor }}
      ref={containerRef}
    >
      {isDesktop && (
        <SlideControls
          total={entry?.slidesCollection?.items.length ?? 0}
          value={slideControlsMotionValue}
          onSelect={(index) => {
            const slide = containerRef.current?.querySelector(
              `#${CSS.escape(slideGroupId)}-${index}`
            ) as HTMLElement;
            if (slide) lenis?.scrollTo(slide);
          }}
        />
      )}
      <div style={{ color: slideThemes[0].foreground }}>
        <ProductMotionInitialSlide entry={entry} />
      </div>
      {(entry?.slidesCollection?.items ?? []).map((slide, index) => {
        if (!slide) return null;
        return (
          <div style={{ color: slideThemes[index].foreground }} key={index}>
            <ProductMotionSlide
              slide={slide}
              index={index}
              id={`${slideGroupId}-${index}`}
            />
          </div>
        );
      })}
    </motion.section>
  );
};
